import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './support.css';
import API_BASE_URL from '../../config/api';
import SupportMenu from './SupportMenu';

const NOTICE_PER_PAGE = 6;

const Support = () => {
    const [notices, setNotices] = useState([]);
    const [search, setSearch] = useState('');
    const [searchType, setSearchType] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchNotices = async () => {
            try {
                setLoading(true);
                setError('');

                const response = await fetch(`${API_BASE_URL}/api/notices`);

                if (!response.ok) {
                    throw new Error('공지사항 조회에 실패했습니다.');
                }

                const data = await response.json();

                const formattedNotices = data.map((notice) => ({
                    id: notice.notice_id,
                    type: notice.notice_type,
                    title: notice.title,
                    date: new Date(notice.created_at).toLocaleDateString(
                        'ko-KR',
                        {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit'
                        }
                    ).replace(/\. /g, '.').replace(/\.$/, ''),
                    views: notice.view_count
                }));

                setNotices(formattedNotices);
            } catch (error) {
                console.error('공지사항 조회 오류:', error);
                setError('공지사항을 불러오지 못했습니다.');
            } finally {
                setLoading(false);
            }
        };

        fetchNotices();
    }, []);

    const filteredNotices = useMemo(() => {
        const keyword = search.trim().toLowerCase();

        if (!keyword) {
            return notices;
        }

        return notices.filter((notice) => {
            if (searchType === 'title') {
                return notice.title
                    .toLowerCase()
                    .includes(keyword);
            }

            if (searchType === 'type') {
                return notice.type
                    .toLowerCase()
                    .includes(keyword);
            }

            return (
                notice.title.toLowerCase().includes(keyword) ||
                notice.type.toLowerCase().includes(keyword)
            );
        });
    }, [notices, search, searchType]);

    const totalPages = Math.max(
        1,
        Math.ceil(filteredNotices.length / NOTICE_PER_PAGE)
    );

    const startIndex =
        (currentPage - 1) * NOTICE_PER_PAGE;

    const currentNotices = filteredNotices.slice(
        startIndex,
        startIndex + NOTICE_PER_PAGE
    );

    const handleSearch = (event) => {
        setSearch(event.target.value);
        setCurrentPage(1);
    };

    const handleSearchType = (event) => {
        setSearchType(event.target.value);
        setCurrentPage(1);
    };

    const movePage = (page) => {
        if (page < 1 || page > totalPages) {
            return;
        }

        setCurrentPage(page);

        window.scrollTo({
            top: 400,
            behavior: 'smooth'
        });
    };

    return (
        <div className="support-page">
            <section className="support-sub-visual">
                <div className="support-sub-overlay"></div>

                <div className="support-sub-inner">
                    <p>CUSTOMER SUPPORT</p>

                    <h1>공지사항</h1>

                    {/* <span>
                        빠르고 정확한 상담을 통해
                        <br />
                        최적의 솔루션을 제공해드립니다.
                    </span> */}

                    <div className="support-breadcrumb">
                        <Link to="/">
                            HOME
                        </Link>

                        <span>›</span>

                        <strong>
                            공지사항
                        </strong>
                    </div>
                </div>
            </section>

            <SupportMenu />

            <section className="support-content">
                <div className="support-container">
                    <div className="support-main">
                        <div className="support-heading">
                            <p>NOTICE</p>

                            <h2>
                                공지사항
                            </h2>

                            <span>
                                MTMEDIA의 새로운 소식과
                                중요한 안내사항을 확인하실 수 있습니다.
                            </span>
                        </div>

                        <div className="support-search">
                            <select
                                value={searchType}
                                onChange={handleSearchType}
                                aria-label="검색 조건"
                            >
                                <option value="all">
                                    전체
                                </option>

                                <option value="title">
                                    제목
                                </option>

                                <option value="type">
                                    분류
                                </option>
                            </select>

                            <div className="support-search-input">
                                <input
                                    type="text"
                                    value={search}
                                    onChange={handleSearch}
                                    placeholder="검색어를 입력하세요."
                                />

                                <i className="mdi mdi-magnify"></i>
                            </div>
                        </div>

                        <div className="support-notice-table">
                            <div className="support-notice-head">
                                <span>번호</span>
                                <span>제목</span>
                                <span>작성일</span>
                                <span>조회수</span>
                            </div>

                            {loading ? (
                                <div className="support-notice-empty">
                                    공지사항을 불러오는 중입니다.
                                </div>
                            ) : error ? (
                                <div className="support-notice-empty">
                                    {error}
                                </div>
                            ) : currentNotices.length > 0 ? (
                                currentNotices.map((notice) => (
                                    <Link
                                        to={`/support/notice/${notice.id}`}
                                        className="support-notice-row"
                                        key={notice.id}
                                    >
                                        <span>
                                            {notice.id}
                                        </span>

                                        <strong>
                                            <em>
                                                [{notice.type}]
                                            </em>

                                            {notice.title}
                                        </strong>

                                        <span>
                                            {notice.date}
                                        </span>

                                        <span>
                                            {notice.views}
                                        </span>
                                    </Link>
                                ))
                            ) : (
                                <div className="support-notice-empty">
                                    검색 결과가 없습니다.
                                </div>
                            )}
                        </div>

                        {!loading && !error && totalPages > 1 && (
                            <div className="support-pagination">
                                <button
                                    type="button"
                                    onClick={() =>
                                        movePage(currentPage - 1)
                                    }
                                    disabled={currentPage === 1}
                                    aria-label="이전 페이지"
                                >
                                    <i className="mdi mdi-chevron-left"></i>
                                </button>

                                {Array.from(
                                    { length: totalPages },
                                    (_, index) => index + 1
                                ).map((page) => (
                                    <button
                                        type="button"
                                        key={page}
                                        className={
                                            currentPage === page
                                                ? 'active'
                                                : ''
                                        }
                                        onClick={() => movePage(page)}
                                    >
                                        {page}
                                    </button>
                                ))}

                                <button
                                    type="button"
                                    onClick={() =>
                                        movePage(currentPage + 1)
                                    }
                                    disabled={
                                        currentPage === totalPages
                                    }
                                    aria-label="다음 페이지"
                                >
                                    <i className="mdi mdi-chevron-right"></i>
                                </button>
                            </div>
                        )}
                    </div>

                    <aside className="support-side">
                        <div className="support-contact-card">
                            <div className="support-side-title">
                                <strong>
                                    빠른 상담 안내
                                </strong>

                                <span>
                                    제품 문의, 견적 상담 등
                                    언제든지 문의해주세요.
                                </span>
                            </div>

                            <div className="support-phone">
                                <div className="support-phone-icon">
                                    <i className="mdi mdi-phone"></i>
                                </div>

                                <div>
                                    <span>
                                        고객센터
                                    </span>

                                    <a href="tel:01090453707">
                                        010-9045-3707
                                    </a>

                                    <p>
                                        평일 09:00 ~ 18:00
                                        <br />
                                        주말 · 공휴일 휴무
                                    </p>
                                </div>
                            </div>

                            <Link
                                to="/inquiry"
                                className="support-contact-button primary"
                            >
                                <i className="mdi mdi-message-text-outline"></i>

                                <span>
                                    견적문의 바로가기
                                </span>

                                <i className="mdi mdi-arrow-right"></i>
                            </Link>
                        </div>

                        <div className="support-quick-card">
                            <div className="support-side-title">
                                <strong>
                                    자주 찾는 메뉴
                                </strong>

                                <span>
                                    원하시는 메뉴를 빠르게 이용해보세요.
                                </span>
                            </div>

                            <div className="support-quick-grid">
                                <Link to="/support">
                                    <i className="mdi mdi-file-document-outline"></i>

                                    <span>
                                        공지사항
                                    </span>

                                    <i className="mdi mdi-arrow-right"></i>
                                </Link>

                                <Link to="/support/faq">
                                    <i className="mdi mdi-help-circle-outline"></i>

                                    <span>
                                        자주 묻는 질문
                                    </span>

                                    <i className="mdi mdi-arrow-right"></i>
                                </Link>

                                <Link to="/inquiry">
                                    <i className="mdi mdi-file-document-edit-outline"></i>

                                    <span>
                                        견적문의
                                    </span>

                                    <i className="mdi mdi-arrow-right"></i>
                                </Link>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
};

export default Support;